import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Home, Search, Heart, User, MapPin, LayoutDashboard, CalendarDays, MessageSquare, LogOut, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../AuthContext';
import NotificationBell from './NotificationBell';

export default function Layout() {
  const { user, isAuthed, isOrganizer, orgMode, setOrgMode, logout } = useAuth();
  const navigate = useNavigate();

  const customerLinks = [
    { to: '/', icon: Home, label: 'Home' },
    { to: '/search', icon: Search, label: 'Search' },
    { to: '/map', icon: MapPin, label: 'Map' },
    { to: '/wishlist', icon: Heart, label: 'Wishlist' },
    { to: '/profile', icon: User, label: 'Profile' },
  ];

  const organizerLinks = [
    { to: '/organizer/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/organizer/events', icon: CalendarDays, label: 'Events' },
    { to: '/organizer/enquiries', icon: MessageSquare, label: 'Enquiries' },
    { to: '/organizer/profile', icon: User, label: 'Profile' },
  ];

  const links = orgMode ? organizerLinks : customerLinks;

  const handleSwitch = async () => {
    if (orgMode) {
      await setOrgMode(false);
      navigate('/');
    } else {
      await setOrgMode(true);
      navigate('/organizer/dashboard');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/welcome');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <button
            data-testid="nav-logo"
            onClick={() => navigate(orgMode ? '/organizer/dashboard' : '/')}
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
          >
            <img src="/radius-icon.png" alt="RADIUS" className="w-9 h-9 rounded-xl object-cover" />
            <span className="text-lg font-black tracking-wider text-gray-900">RADIUS</span>
            {orgMode && (
              <span className="text-[10px] font-bold bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">
                ORGANIZER
              </span>
            )}
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/' || l.to === '/organizer/dashboard'}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                    isActive
                      ? 'bg-pink-50 text-pink-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {isOrganizer && (
              <button
                data-testid="nav-switch-mode"
                onClick={handleSwitch}
                className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-brand-magenta bg-pink-50 border border-pink-200 px-3 py-1.5 rounded-full hover:bg-pink-100 transition-colors"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                {orgMode ? 'Customer' : 'Organizer'}
              </button>
            )}
            <NotificationBell />
            {isAuthed ? (
              <div className="flex items-center gap-2">
                <button
                  data-testid="nav-user-avatar"
                  onClick={() => navigate(orgMode ? '/organizer/profile' : '/profile')}
                  className="w-9 h-9 rounded-full bg-brand-magenta flex items-center justify-center text-white font-black text-sm hover:bg-pink-700 transition-colors"
                >
                  {user?.full_name?.[0]?.toUpperCase() || 'U'}
                </button>
                <button
                  data-testid="nav-logout-btn"
                  onClick={handleLogout}
                  className="hidden sm:flex w-9 h-9 rounded-full border border-gray-200 items-center justify-center hover:bg-red-50 hover:border-red-200 transition-colors"
                  title="Log out"
                >
                  <LogOut className="w-4 h-4 text-gray-500 hover:text-red-500" />
                </button>
              </div>
            ) : (
              <button
                data-testid="nav-signin-btn"
                onClick={() => navigate('/login')}
                className="bg-brand-magenta text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-pink-700 transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center justify-around h-16 px-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/' || l.to === '/organizer/dashboard'}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl text-[11px] font-bold transition-colors ${
                  isActive ? 'text-brand-magenta' : 'text-gray-400'
                }`
              }
            >
              <l.icon className="w-5 h-5" />
              {l.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
